/* eslint-disable react-refresh/only-export-components --
 * Context module: Provider + hook are intentionally co-located.
 */
import React, { createContext, useContext, useMemo } from 'react';
import type { FormValue, Question, OASISFormData } from '../../types';
import { buildQuestionTreeMap } from '../../walkQuestionTree';
import type { BespokeFormSectionProps } from '../bespokeRegistry';

interface BespokeSectionContextValue {
  answers: Record<string, FormValue>;
  onAnswerChange: (qid: string, value: FormValue) => void;
  onFormDataChange: (updates: Partial<OASISFormData>) => void;
  schemaMap: Map<string, Question>;
}

const BespokeSectionContext = createContext<BespokeSectionContextValue | null>(
  null
);

export function useBespokeSectionContext(): BespokeSectionContextValue {
  const ctx = useContext(BespokeSectionContext);
  if (!ctx)
    throw new Error(
      'OASIS primitives must be used inside <BespokeSectionProvider>'
    );
  return ctx;
}

type BespokeSectionProviderProps = BespokeFormSectionProps & {
  children: React.ReactNode;
};

export function buildSchemaMap(questions: Question[]): Map<string, Question> {
  return buildQuestionTreeMap(questions);
}

export function BespokeSectionProvider({
  answers,
  onAnswerChange,
  onFormDataChange,
  schema,
  children,
}: BespokeSectionProviderProps) {
  const schemaMap = useMemo(() => buildSchemaMap(schema), [schema]);

  const value = useMemo(
    () => ({ answers, onAnswerChange, onFormDataChange, schemaMap }),
    [answers, onAnswerChange, onFormDataChange, schemaMap]
  );

  return (
    <BespokeSectionContext.Provider value={value}>
      {children}
    </BespokeSectionContext.Provider>
  );
}
